<template>
  <v-overlay v-model="centerLoading" :scrim="false" class="custom_centered_overlay">
    <v-progress-circular color="green" indeterminate size="64"></v-progress-circular>
  </v-overlay>

  <v-app id="app_add_products">
    <v-container fluid class="pa-8">
      <v-row align="center">
        <v-col cols="7">
          <p class="text-h5 font-weight-bold mb-0">ADD PRODUCTS WITH STOCKS</p>
          <p class="text-subtitle-2 text-grey">Enter all products in your store along with their stock information.</p>
        </v-col>
        <v-col cols="5" class="text-right">
              <v-btn 
                color="#800080" 
                size="x-large" 
                variant="text" 
                @click="$router.push('/products-category')"
              >
                <v-icon>mdi-plus-circle</v-icon> ADD CATEGORY
              </v-btn>
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
              <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
            </v-btn>
        </v-col>
      </v-row>

      <v-card class="mt-6 custom-square-card" elevation="0" border>
        <v-card-title class="pa-4 text-subtitle-2 font-weight-bold text-uppercase text-grey-darken-1">
          Add Products & Stocks Details
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form">
            <v-row>
              <v-col cols="12" md="6">
                <div>
                  <v-text-field
                    v-model="form.productTitle"
                    label="Product Name *"
                    :placeholder="focused.productTitle ? 'Type title here (max 200 characters)' : ''"
                    variant="outlined"
                    density="comfortable"
                    class="square-border-input"
                    :rules="[
                      v => !!v || 'Product Name is required',
                      v => (v && v.length <= 200) || 'Product Name cannot exceed 200 characters',
                      v => (v && v.length >= 2) || 'Product Name must be at least 2 characters long',
                      v => !/^\s/.test(v) || 'Product Name cannot start with a space',
                      v => !/\s$/.test(v) || 'Product Name cannot end with a space',
                      v => !/\s{2,}/.test(v) || 'Product Name cannot contain multiple consecutive spaces'
                    ]"
                    @input="validateProductName"
                    @focus="focused.productTitle = true"
                    @blur="focused.productTitle = false"
                    maxlength="200"
                    counter
                  ></v-text-field>
                  <div class="d-flex justify-end mt-n2 mb-2">
                    <span class="text-caption" :class="productNameLength > 180 ? 'text-warning' : 'text-grey'">
                      {{ productNameLength }} / 200 characters
                    </span>
                  </div>
                </div>

                <v-autocomplete
                  v-model="form.mainCategory"
                  :items="mainCategories"
                  item-title="name"
                  item-value="id"
                  label="Main Category *"
                  :placeholder="focused.mainCategory ? 'Select main category' : ''"
                  variant="outlined"
                  density="comfortable"
                  class="mt-2 square-border-input"
                  :rules="[v => !!v || 'Main Category is required']"
                  @update:modelValue="fetchSubCategories"
                  @focus="focused.mainCategory = true"
                  @blur="focused.mainCategory = false"
                ></v-autocomplete>

                <v-autocomplete
                  v-model="form.subCategory"
                  :items="subCategories"
                  item-title="sub_category_name"
                  item-value="sub_category_name"
                  label="Sub Category *"
                  :placeholder="focused.subCategory ? 'Select sub category' : ''"
                  variant="outlined"
                  density="comfortable"
                  class="mt-2 square-border-input"
                  :loading="productLoading"
                  :rules="[v => !!v || 'Sub Category is required']"
                  @focus="focused.subCategory = true"
                  @blur="focused.subCategory = false"
                ></v-autocomplete>

                <v-text-field
                  v-model="form.price"
                  label="Unit Price *"
                  :placeholder="focused.price ? 'Type unit price here (0 to 99,999,999.99)' : ''"
                  variant="outlined"
                  density="comfortable"
                  class="mt-2 square-border-input"
                  type="number"
                  min="0"
                  max="99999999.99"
                  step="0.01"
                  :rules="[
                    v => (v !== null && v !== undefined && v !== '') || 'Unit Price is required',
                    v => parseFloat(v) >= 0 || 'Unit Price cannot be negative',
                    v => parseFloat(v) <= 99999999.99 || 'Unit Price cannot exceed 99,999,999.99',
                    v => /^\d*\.?\d{0,2}$/.test(String(v)) || 'Unit Price can have up to 2 decimal places'
                  ]"
                  @input="validatePrice"
                  @focus="focused.price = true"
                  @blur="formatPrice"
                ></v-text-field>

                <div class="mt-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <p class="text-subtitle-2 text-grey-darken-1 mb-0">Product Description <span class="text-black">*</span></p>
                    <span class="text-caption" :class="descriptionCharCount > 5000 ? 'text-red' : 'text-grey'">
                      {{ descriptionCharCount }} / 5000 characters
                    </span>
                  </div>
                  <v-card border flat class="custom-square-card" :class="{ 'editor-error': showDescriptionError }">
                    <Toolbar :editor="editorRef" :defaultConfig="toolbarConfig" :mode="mode" border-bottom />
                    <Editor
                      v-model="form.productDescription"
                      :defaultConfig="editorConfig"
                      :mode="mode"
                      style="height: 200px; overflow-y: hidden;"
                      @onCreated="handleCreated"
                      @onChange="handleDescriptionChange"
                    />
                  </v-card>
                  <p v-if="showDescriptionError" class="text-caption text-red mt-1">Product description is required.</p>
                  
                  <v-alert 
                    v-if="descriptionCharCount > 5000" 
                    type="error" 
                    variant="tonal" 
                    dense 
                    class="mt-2 custom-square-card"
                  >
                    Product description exceeds 5000 character limit. Please reduce the content.
                  </v-alert>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="form.unit"
                  :items="allUnits"
                  item-title="title"
                  item-value="value"
                  label="Unit *"
                  :placeholder="focused.unit ? 'Select measurement unit' : ''"
                  variant="outlined"
                  density="comfortable"
                  class="square-border-input"
                  :rules="[v => !!v || 'Unit is required']"
                  @focus="focused.unit = true"
                  @blur="focused.unit = false"
                  menu-icon="mdi-menu-down"
                  :readonly="false"
                ></v-select>

                <v-text-field
                  v-model="form.color"
                  label="Color *"
                  :placeholder="focused.color ? 'Select product color' : ''"
                  variant="outlined"
                  density="comfortable"
                  class="mt-2 square-border-input"
                  readonly
                  :rules="[v => !!v || 'Color is required']"
                  @click="dialog = true"
                  @focus="focused.color = true"
                  @blur="focused.color = false"
                >
                  <template v-slot:append-inner>
                    <v-avatar :color="form.color" size="24" border></v-avatar>
                  </template>
                </v-text-field>

                <v-text-field
                  v-model="form.availableStock"
                  label="Available Stock (Quantity) *"
                  :placeholder="focused.availableStock ? 'Enter stock quantity (0 to 999,999)' : ''"
                  variant="outlined"
                  density="comfortable"
                  class="mt-2 square-border-input"
                  type="number"
                  min="0"
                  max="999999"
                  step="1"
                  :rules="[
                    v => (v !== null && v !== undefined && v !== '') || 'Available Stock is required',
                    v => /^\d+$/.test(String(v)) || 'Only positive whole numbers are allowed',
                    v => Number(v) >= 0 || 'Available Stock cannot be negative. Please enter zero or a positive number',
                    v => Number(v) <= 999999 || 'Available Stock cannot exceed 999,999',
                    v => Number.isInteger(Number(v)) || 'Stock must be a whole number (no decimals)'
                  ]"
                  @input="validateStock"
                  @focus="focused.availableStock = true"
                  @blur="formatStock"
                ></v-text-field>

                <v-text-field
                  v-model="form.discountRate"
                  label="Discount Rate (%)"
                  :placeholder="focused.discountRate ? 'Enter discount percentage (e.g., 10.50)' : ''"
                  variant="outlined"
                  density="comfortable"
                  class="mt-2 square-border-input"
                  :rules="[
                    v => !v || /^\d*\.?\d{0,2}$/.test(v) || 'Discount Rate can have up to 2 decimal places',
                    v => !v || parseFloat(v) >= 0 || 'Discount Rate cannot be negative',
                    v => !v || parseFloat(v) <= 100 || 'Discount Rate cannot exceed 100%',
                    v => !v || !isNaN(parseFloat(v)) || 'Please enter a valid number'
                  ]"
                  @input="validateDiscountRate"
                  @focus="focused.discountRate = true"
                  @blur="formatDiscountRate"
                ></v-text-field>

                <div class="mt-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <p class="text-subtitle-2 text-grey-darken-1 mb-0">
                      Product Images <span class="text-black">*</span>
                    </p>
                    <span class="text-caption text-grey">
                      {{ previewImages.length }} / 5 images
                    </span>
                  </div>
                  <div 
                    class="image-upload-zone" 
                    :class="{ 'has-images': previewImages.length > 0, 'image-error': showImageError }"
                    @click="triggerFileInput"
                    @drop.prevent="onDrop"
                    @dragover.prevent="onDragOver"
                  >
                    <input ref="fileInput" type="file" hidden multiple accept="image/*" @change="onFileChange" />
                    
                    <div v-if="previewImages.length === 0" class="text-center pa-10">
                      <v-icon size="48" :color="showImageError ? 'red' : 'grey-lighten-1'">mdi-image-plus</v-icon>
                      <p class="mt-2" :class="showImageError ? 'text-red' : 'text-grey'">Drag your photo here OR <span class="font-weight-bold" :class="showImageError ? 'text-red' : 'text-primary'">Browse from device</span></p>
                      <p class="text-caption text-red mt-1" v-if="showImageError">Product image is required. Please upload at least one image.</p>
                    </div>

                    <v-row v-else class="pa-2">
                      <v-col v-for="(img, idx) in previewImages" :key="idx" cols="4" class="position-relative">
                        <v-img :src="img" aspect-ratio="1" cover class="rounded-lg border">
                          <div class="delete-overlay">
                            <v-btn icon="mdi-delete" color="red" size="small" @click.stop="removeImage(idx)"></v-btn>
                          </div>
                        </v-img>
                      </v-col>
                      <v-col v-if="previewImages.length < 5" cols="4" class="d-flex align-center justify-center">
                        <v-btn icon="mdi-plus" variant="outlined" color="grey"></v-btn>
                      </v-col>
                    </v-row>
                  </div>
                  <p v-if="previewImages.length >= 5" class="text-caption text-red mt-1">Maximum 5 images allowed</p>
                  <p v-if="previewImages.length > 0 && previewImages.length < 5" class="text-caption text-grey mt-1">
                    * You can add up to {{ 5 - previewImages.length }} more image(s)
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
            <v-btn
              v-if="permissions.write"
              style="background-color: #800080; color: white;"
              class="px-8 custom-square-card"
              size="large"
              @click="addProduct"
              :disabled="loading"
            >
              <v-progress-circular v-if="loading" indeterminate size="20" color="white" class="mr-2"></v-progress-circular>
              CREATE PRODUCT
            </v-btn>

            <v-btn
              variant="outlined"
              color="grey"
              class="px-8 ml-3 custom-square-card"
              size="large"
              @click="clearForm"
              :disabled="loading"
            >
              CANCEL
            </v-btn>
        </v-card-actions>
      </v-card>

      <v-dialog v-model="dialog" max-width="350px">
        <v-card class="custom-square-card">
          <v-card-title class="d-flex justify-space-between align-center pa-4">
            <span class="text-subtitle-1 font-weight-bold">SELECT COLOR</span>
            <v-btn icon="mdi-close" variant="text" size="small" @click="dialog = false"></v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <div class="pa-4 d-flex flex-column align-center">
            <v-color-picker v-model="form.color" hide-inputs show-swatches></v-color-picker>
            <v-text-field v-model="form.color" variant="outlined" density="compact" class="mt-4 w-100 square-border-input" label="Hex Code"></v-text-field>
            <v-btn color="#800080" block class="mt-2 custom-square-card" style="color: white;" @click="dialog = false">DONE</v-btn>
          </div>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackAlert.snackbar" :color="snackAlert.snackColor" location="top right" :timeout="3000">
        <span class="text-white font-weight-medium">{{ snackAlert.snackMessage }}</span>
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import ntc from 'ntc';
import '@wangeditor/editor/dist/css/style.css';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import { onBeforeUnmount, reactive, shallowRef } from 'vue'
import { getPermissions } from "@/plugins/permissions";

export default {
  name: 'AddProducts',
  components: { Editor, Toolbar },
  
  setup() {
    const editorRef = shallowRef();
    
    const toolbarConfig = { 
      toolbarKeys: ['bold', 'italic', 'underline', 'through', 'bulletedList', 'numberedList', 'insertLink'],
      excludeKeys: [
        'justifyFull', 
        'group-justify'
      ] 
    };
    
    const editorConfig = { placeholder: 'Write a description here...' };

    onBeforeUnmount(() => {
      const editor = editorRef.value;
      if (editor == null) return;
      editor.destroy();
    });

    const handleCreated = (editor) => {
      editorRef.value = editor;
    };

    const focused = reactive({
      productTitle: false,
      mainCategory: false,
      subCategory: false,
      price: false,
      unit: false,
      color: false,
      availableStock: false,
      discountRate: false
    });

    const formData = reactive({
      productDescription: ''
    });

    const handleDescriptionChange = (editor) => {
      const content = editor.getHtml();
      const textContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      if (window && window.updateCharCount) {
        window.updateCharCount(textContent);
      }
    };

    return { 
      editorRef, 
      mode: 'default', 
      toolbarConfig, 
      editorConfig, 
      handleCreated,
      focused,
      formData,
      handleDescriptionChange
    };
  },
  
  data() {
    return {
      permissions: { read: false, write: false, delete: false },
      centerLoading: false,
      productLoading: false,
      loading: false,
      dialog: false,
      descriptionCharCount: 0,
      productNameLength: 0,
      showImageError: false,
      showDescriptionError: false,
      measurementUnits: [
        { title: 'Grams', value: 'g', category: 'Weight' },
        { title: 'Kilograms', value: 'Kg', category: 'Weight' },
        { title: 'Liters', value: 'L', category: 'Volume' },
        { title: 'Milliliters', value: 'Ml', category: 'Volume' },
        { title: 'Centimeters', value: 'Cm', category: 'Length' },
        { title: 'Meters', value: 'M', category: 'Length' }
      ],
      nonMeasurementUnits: [
        { title: 'Piece', value: 'Piece', category: 'Quantity' },
        { title: 'Box', value: 'Box', category: 'Quantity' },
        { title: 'Set', value: 'Set', category: 'Quantity' },
        { title: 'Pack', value: 'Pack', category: 'Quantity' },
        { title: 'Bundle', value: 'Bundle', category: 'Quantity' },
        { title: 'Dozen', value: 'Dozen', category: 'Quantity' },
        { title: 'Pair', value: 'Pair', category: 'Quantity' },
        { title: 'Roll', value: 'Roll', category: 'Quantity' },
        { title: 'Sheet', value: 'Sheet', category: 'Quantity' },
        { title: 'Carton', value: 'Carton', category: 'Quantity' },
        { title: 'Bottle', value: 'Bottle', category: 'Quantity' },
        { title: 'Can', value: 'Can', category: 'Quantity' },
        { title: 'Bag', value: 'Bag', category: 'Quantity' },
        { title: 'Container', value: 'Container', category: 'Quantity' },
        { title: 'Drum', value: 'Drum', category: 'Quantity' },
        { title: 'Pallet', value: 'Pallet', category: 'Quantity' },
        { title: 'Ream', value: 'Ream', category: 'Quantity' },
        { title: 'Tub', value: 'Tub', category: 'Quantity' },
        { title: 'Vial', value: 'Vial', category: 'Quantity' },
        { title: 'Bunch', value: 'Bunch', category: 'Quantity' }
      ],
      allUnits: [],
      mainCategories: [],
      subCategories: [],
      previewImages: [],
      snackAlert: {
        snackbar: false,
        snackColor: "",
        snackMessage: ""
      },
      form: {
        productTitle: '',
        mainCategory: null,
        main_category_name: '',
        subCategory: null,
        unit: null,
        price: null,
        color: '#6200EE',
        availableStock: null,
        discountRate: null,
        productDescription: '',
        images: []
      }
    };
  },
  
  watch: {
    'form.productDescription': {
      handler(newVal) {
        if (newVal) {
          const textContent = newVal.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
          this.descriptionCharCount = textContent.length;
          if (textContent.length > 0) {
            this.showDescriptionError = false;
          }
        } else {
          this.descriptionCharCount = 0;
        }
      },
      immediate: true
    },
    'form.productTitle': {
      handler(newVal) {
        if (newVal) {
          this.productNameLength = newVal.length;
        } else {
          this.productNameLength = 0;
        }
      },
      immediate: true
    }
  },
  
  created() {
    this.allUnits = [
      { 
        title: 'Weight Units', 
        value: 'separator', 
        disabled: true,
        divider: true,
        header: true 
      },
      ...this.measurementUnits.map(unit => ({ 
        title: `${unit.title} (${unit.value})`, 
        value: unit.value,
        category: unit.category 
      })),
      { 
        title: 'Quantity Units', 
        value: 'separator2', 
        disabled: true,
        divider: true,
        header: true 
      },
      ...this.nonMeasurementUnits.map(unit => ({ 
        title: `${unit.title} (${unit.value})`, 
        value: unit.value,
        category: unit.category 
      }))
    ];
  },
  
  methods: {
    updateCharacterCount(text) {
      this.descriptionCharCount = text.length;
    },

    validateProductName() {
      if (this.form.productTitle) {
        let cleaned = this.form.productTitle.replace(/\s{2,}/g, ' ');
        cleaned = cleaned.trim();
        
        if (this.form.productTitle !== cleaned) {
          this.form.productTitle = cleaned;
        }
        
        this.productNameLength = this.form.productTitle.length;
        
        if (this.form.productTitle.length > 200) {
          this.form.productTitle = this.form.productTitle.substring(0, 200);
          this.productNameLength = 200;
          this.showSnackAlert('warning', 'Product Name cannot exceed 200 characters');
        }
      }
    },

    validatePrice() {
      if (this.form.price !== null && this.form.price !== undefined && this.form.price !== '') {
        let price = parseFloat(this.form.price);
        
        if (price < 0) {
          this.form.price = 0;
          this.showSnackAlert('warning', 'Unit Price cannot be negative');
          return;
        }
        
        if (price > 99999999.99) {
          this.form.price = 99999999.99;
          this.showSnackAlert('warning', 'Maximum price limit is 99,999,999.99');
          return;
        }
        
        if (this.form.price && this.form.price.toString().includes('.')) {
          const parts = this.form.price.toString().split('.');
          if (parts[1] && parts[1].length > 2) {
            this.form.price = parseFloat(this.form.price).toFixed(2);
            this.showSnackAlert('warning', 'Price limited to 2 decimal places');
          }
        }
      }
    },

    formatPrice() {
      if (this.form.price !== null && this.form.price !== undefined && this.form.price !== '') {
        let price = parseFloat(this.form.price);
        if (!isNaN(price)) {
          if (price < 0) {
            price = 0;
            this.showSnackAlert('warning', 'Unit Price cannot be negative');
          }
          if (price > 99999999.99) {
            price = 99999999.99;
            this.showSnackAlert('warning', 'Maximum price limit is 99,999,999.99');
          }
          this.form.price = price.toFixed(2);
        }
      }
    },

    validateStock() {
      if (this.form.availableStock !== null && this.form.availableStock !== undefined && this.form.availableStock !== '') {
        let stock = this.form.availableStock.toString();
        
        stock = stock.replace(/[^\d]/g, '');
        stock = stock.replace(/^0+/, '');
        
        if (stock === '') {
          stock = '0';
        }
        
        let stockNumber = parseInt(stock, 10);
        
        if (stockNumber < 0) {
          this.form.availableStock = 0;
          this.showSnackAlert('warning', 'Available Stock cannot be negative');
        }
        else if (stockNumber > 999999) {
          this.form.availableStock = 999999;
          this.showSnackAlert('warning', 'Available Stock cannot exceed 999,999');
        }
        else if (isNaN(stockNumber)) {
          this.form.availableStock = 0;
        }
        else {
          this.form.availableStock = stockNumber;
        }
      }
    },

    formatStock() {
      if (this.form.availableStock !== null && this.form.availableStock !== undefined && this.form.availableStock !== '') {
        let stock = parseInt(this.form.availableStock, 10);
        if (!isNaN(stock)) {
          if (stock > 999999) {
            stock = 999999;
            this.showSnackAlert('warning', 'Available Stock cannot exceed 999,999');
          }
          if (stock < 0) {
            stock = 0;
            this.showSnackAlert('warning', 'Available Stock cannot be negative');
          }
          this.form.availableStock = stock;
        }
      }
    },

    validateDiscountRate() {
      if (this.form.discountRate !== null && this.form.discountRate !== undefined && this.form.discountRate !== '') {
        let value = this.form.discountRate.toString();
        value = value.replace(/[^\d.]/g, '');
        
        const parts = value.split('.');
        if (parts.length > 2) {
          value = parts[0] + '.' + parts.slice(1).join('');
        }
        
        this.form.discountRate = value;
        let discount = parseFloat(value);
        
        if (!isNaN(discount)) {
          if (discount > 100) {
            this.form.discountRate = 100;
            discount = 100;
            this.showSnackAlert('warning', 'Discount Rate cannot exceed 100%');
          }
          
          if (discount < 0) {
            this.form.discountRate = 0;
            discount = 0;
            this.showSnackAlert('warning', 'Discount Rate cannot be negative');
          }
          
          if (value.includes('.')) {
            const decimalParts = value.split('.');
            if (decimalParts[1] && decimalParts[1].length > 2) {
              this.form.discountRate = discount.toFixed(2);
              this.showSnackAlert('warning', 'Discount Rate limited to 2 decimal places');
            }
          }
        } else if (value !== '') {
          this.form.discountRate = '';
        }
      }
    },

    formatDiscountRate() {
      if (this.form.discountRate !== null && this.form.discountRate !== undefined && this.form.discountRate !== '') {
        let discount = parseFloat(this.form.discountRate);
        if (!isNaN(discount)) {
          this.form.discountRate = discount.toFixed(2);
        }
      }
    },

    fetchMainCategories() {
      const token = localStorage.getItem('token');
      axios.post('/api/mainProduct', { userId: localStorage.getItem('userId') }, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        this.mainCategories = res.data.data.map(item => ({ id: item.id, name: item.main_category }));
      }).catch(err => {
        console.error('Error fetching main categories:', err);
        if (err.response && err.response.status === 401) {
          this.handleLogout();
        }
      });
    },
    
    fetchSubCategories() {
      this.productLoading = true;
      const token = localStorage.getItem('token');
      const selectedMain = this.mainCategories.find(c => c.id === this.form.mainCategory);
      this.form.main_category_name = selectedMain ? selectedMain.name : '';

      axios.post(`/api/subcatagoryProduct`, {
        userId: localStorage.getItem('userId'),
        mainCatagoryId: this.form.mainCategory
      }, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        this.subCategories = res.data.data;
        this.productLoading = false;
      }).catch(err => {
        this.productLoading = false;
        console.error('Error fetching sub categories:', err);
        if (err.response && err.response.status === 401) {
          this.handleLogout();
        }
      });
    },
    
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    
    onFileChange(e) {
      this.handleFiles(Array.from(e.target.files));
      e.target.value = '';
    },
    
    onDrop(e) {
      this.handleFiles(Array.from(e.dataTransfer.files));
    },
    
    onDragOver(e) {
      e.preventDefault();
    },
    
    handleFiles(files) {
      const limit = 5 - this.previewImages.length;
      const validFiles = files.filter(f => f.type.startsWith('image/')).slice(0, limit);
      
      if (validFiles.length === 0 && files.length > 0) {
        this.showSnackAlert('error', 'Please upload valid image files (JPG, PNG, GIF, etc.)');
        return;
      }
      
      validFiles.forEach(file => {
        if (file.size > 5 * 1024 * 1024) {
          this.showSnackAlert('error', 'Each image must be less than 5MB');
          return;
        }
        this.form.images.push(file);
        this.previewImages.push(URL.createObjectURL(file));
      });
      
      if (this.form.images.length > 0) {
        this.showImageError = false;
      }
    },
    
    removeImage(index) {
      this.form.images.splice(index, 1);
      this.previewImages.splice(index, 1);
    },
    
    clearForm() {
      this.form = {
        productTitle: '',
        mainCategory: null,
        main_category_name: '',
        subCategory: null,
        unit: null,
        price: null,
        color: '#6200EE',
        availableStock: null,
        discountRate: null,
        productDescription: '',
        images: []
      };
      
      this.previewImages = [];
      this.descriptionCharCount = 0;
      this.productNameLength = 0;
      this.showImageError = false;
      this.showDescriptionError = false;
      this.subCategories = [];
      
      if (this.$refs.form) {
        this.$refs.form.reset();
        this.$refs.form.resetValidation();
      }
      
      if (this.editorRef && this.editorRef.value) {
        this.editorRef.value.clear();
      }
      
      Object.keys(this.focused).forEach(key => {
        this.focused[key] = false;
      });
      
      this.showSnackAlert('info', 'Form has been cleared');
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    
    async addProduct() {
      if (this.form.images.length === 0) {
        this.showImageError = true;
      } else {
        this.showImageError = false;
      }

      const descTextContent = this.form.productDescription.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      if (!descTextContent) {
        this.showDescriptionError = true;
      } else {
        this.showDescriptionError = false;
      }

      const { valid } = await this.$refs.form.validate();
      
      if (!valid || this.showImageError || this.showDescriptionError) {
        if (this.showImageError) {
          this.showSnackAlert('error', 'Product image is required. Please upload at least one image.');
          this.$nextTick(() => {
            const imageZone = document.querySelector('.image-upload-zone');
            if (imageZone) {
              imageZone.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
        } else if (this.showDescriptionError) {
          this.showSnackAlert('error', 'Product description is required.');
        } else {
          this.showSnackAlert('error', 'Please fill all required fields correctly.');
        }
        return;
      }
      
      if (this.descriptionCharCount > 5000) {
        this.showSnackAlert('error', 'Product description exceeds 5000 character limit. Please reduce the content.');
        return;
      }

      if (this.form.productTitle && this.form.productTitle.length > 200) {
        this.showSnackAlert('error', 'Product Name cannot exceed 200 characters');
        return;
      }

      if (this.form.price !== null && this.form.price !== undefined) {
        let price = parseFloat(this.form.price);
        if (isNaN(price) || price < 0) {
          this.showSnackAlert('error', 'Unit Price cannot be negative');
          return;
        }
        if (price > 99999999.99) {
          this.showSnackAlert('error', 'Unit Price cannot exceed 99,999,999.99');
          return;
        }
      }

      if (this.form.availableStock !== null && this.form.availableStock !== undefined) {
        let stock = parseInt(this.form.availableStock, 10);
        if (isNaN(stock) || stock < 0) {
          this.showSnackAlert('error', 'Available Stock cannot be negative. Please enter zero or a positive number');
          return;
        }
        if (stock > 999999) {
          this.showSnackAlert('error', 'Available Stock cannot exceed 999,999');
          return;
        }
      }

      if (this.form.discountRate !== null && this.form.discountRate !== undefined && this.form.discountRate !== '') {
        this.form.discountRate = parseFloat(this.form.discountRate).toFixed(2);
      }

      this.loading = true;
      this.centerLoading = true;
      
      try {
        const formData = new FormData();
        formData.append('userId', localStorage.getItem('userId'));
        formData.append('productTitle', this.form.productTitle);
        formData.append('productDescription', this.form.productDescription);
        formData.append('mainCatagory', this.form.main_category_name);
        formData.append('subCatagory', this.form.subCategory);
        formData.append('size', this.form.unit);
        formData.append('color', ntc.name(this.form.color)[1]);
        formData.append('price', this.form.price);
        formData.append('discountRate', this.form.discountRate || 0);
        formData.append('availableStaock', this.form.availableStock);
        formData.append('unit', this.form.unit);
        formData.append('number_of_unit', 1);

        const base64Images = await Promise.all(this.form.images.map(file => this.convertToBase64(file)));
        formData.append('productImage', JSON.stringify(base64Images.map(img => ({ image: img }))));

        const token = localStorage.getItem('token');
        const response = await axios.post(`/api/addProduct`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.success || response.status === 201) {
          this.showSnackAlert('success', 'Product Created Successfully!');
          
          setTimeout(() => {
            this.clearForm();
          }, 1500);

        } else {
          this.showSnackAlert('error', response.data.error || 'Failed to create product');
          this.loading = false;
          this.centerLoading = false;
        }
      } catch (err) {
        console.error('Product creation error:', err);
        if (err.response) {
          if (err.response.status === 401) {
            this.showSnackAlert('error', 'Session expired. Please login again.');
            this.handleLogout();
          } else if (err.response.status === 409) {
            this.showSnackAlert('error', 'Product already exists!');
          } else if (err.response.data && err.response.data.error) {
            this.showSnackAlert('error', err.response.data.error);
          } else {
            this.showSnackAlert('error', 'Server error. Please try again.');
          }
        } else if (err.request) {
          this.showSnackAlert('error', 'Network error. Please check your connection.');
        } else {
          this.showSnackAlert('error', 'An unexpected error occurred.');
        }
        this.loading = false;
        this.centerLoading = false;
      } finally {
        this.loading = false;
        this.centerLoading = false;
      }
    },
    
    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = error => reject(error);
      });
    },
    
    showSnackAlert(type, msg) {
      let bgColor = '#ff5252'; 
      if (type === 'success') {
        bgColor = '#81c784'; 
      } else if (type === 'info') {
        bgColor = '#29b6f6'; 
      } else if (type === 'warning') {
        bgColor = '#fb8c00'; 
      }
      
      this.snackAlert = {
        snackbar: true,
        snackColor: bgColor,
        snackMessage: msg
      };

      setTimeout(() => {
        this.snackAlert.snackbar = false;
      }, 3000);
    },
    
    handleLogout() {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      this.$router.push('/login');
    },
    
    addCategoryPageRoute() { 
      this.$router.push('/products-category'); 
    },
    
    backPageRoute() { 
      this.$router.back(); 
    }
  },
  
  mounted() {
    this.permissions = getPermissions(0, "Module Name");

    if (!this.permissions.read) {
      this.showSnackAlert('error', 'Access Denied');
      this.$router.push('/dashboard');
      return;
    }
    
    this.fetchMainCategories();

    window.updateCharCount = this.updateCharacterCount.bind(this);
  },

  beforeUnmount() {
    if (window.updateCharCount) {
      delete window.updateCharCount;
    }
  }
};
</script>

<style scoped>
.image-upload-zone {
  border: 2px dashed #e0e0e0;
  border-radius: 2px;
  min-height: 180px;
  transition: all 0.3s ease;
  cursor: pointer;
  background: #fcfcfc;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-upload-zone:hover {
  border-color: #156CF7;
  background: #f0f7ff;
}
.image-upload-zone.image-error {
  border: 2px dashed #ff5252 !important;
  background: #ffebee !important;
}
.image-upload-zone.has-images {
  display: block;
  min-height: auto;
}

.editor-error {
  border: 1px solid #ff5252 !important;
  background-color: #fffafb;
}

.delete-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: 0.2s;
}
.position-relative:hover .delete-overlay {
  opacity: 1;
}

.text-red {
  color: #ff5252 !important;
}

.text-warning {
  color: #ff9800 !important;
}

.divider-vertical {
  margin: 0 8px;
}

.gray-text {
  color: #666;
}

.custom_centered_overlay {
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-square-card {
  border-radius: 2px !important;
}

.square-border-input :deep(.v-field),
.square-border-input :deep(.v-field__outline),
.square-border-input :deep(.v-field__overlay),
.square-border-input :deep(.v-field__outline__start),
.square-border-input :deep(.v-field__outline__end),
.square-border-input :deep(.v-field__outline__notch::before),
.square-border-input :deep(.v-field__outline__notch::after) {
  border-radius: 2px !important;
}

@media (max-width: 600px) {
  .v-btn {
    margin: 4px 0;
  }
}
</style>